<script setup lang="ts">
import type { HTMLAttributes } from 'vue';
import SearchableModel from '@/components/searchable-model/SearchableModel.vue';
import OrderIndexTableRow from '@/components/orders/OrderIndexTableRow.vue';
import OrderExportMenu from '@/components/orders/OrderExportMenu.vue';
import { formatMoney } from '@/utils/money';

const props = withDefaults(defineProps<{
  class?: HTMLAttributes['class'],
  addUrl?: string,
  availableFilters?: Array<Record<string, any>>,
  baseFilters?: Record<string, any>,
  pushState?: boolean,
  enableContainer?: boolean,
  /**
   * Where a row opens the order. A listing embedded in another page sends it to a new
   * tab, so the reader keeps the page they were working on.
   */
  target?: '_self' | '_blank',
}>(), {
  class: undefined,
  addUrl: undefined,
  availableFilters: () => [],
  baseFilters: () => ({}),
  pushState: false,
  enableContainer: false,
  target: '_self',
});

/**
 * Plain English, as every other listing declares them. SearchableModel translates a
 * label when it renders it, so these are the keys rather than the wording.
 */
const sortOptions = [
  { value: 'created_at', label: 'Date' },
  { value: 'id', label: 'Order number' },
];
</script>

<template>
  <SearchableModel
    model-name="Order"
    with-search
    :search-placeholder="$t('Search by order number, name, or email address')"
    :add-url="props.addUrl"
    :base-filters="props.baseFilters"
    :available-filters="props.availableFilters"
    :sort-options="sortOptions"
    sort="-created_at"
    :push-state="props.pushState"
    :enable-container="props.enableContainer"
    :class="props.class"
  >
    <template v-if="$slots['header-title']" #header-title>
      <slot name="header-title"></slot>
    </template>

    <template #header-actions="{ exportUrl }">
      <OrderExportMenu :export-url="exportUrl" />
      <slot name="header-actions"></slot>
    </template>

    <template #default="{ models, meta, search }">
      <div class="table-responsive">
        <table v-if="models" class="table table-hover">
          <thead class="tw:text-left tw:text-sm tw:bg-zinc-50">
            <tr>
              <th>{{ $t('Order #') }}</th>
              <th>{{ $t('Created at') }}</th>
              <th>{{ $t('Customer name') }}</th>
              <th class="tw:text-center">{{ $t('Items') }}</th>
              <th>{{ $t('Participants') }}</th>
              <th>{{ $t('Status') }}</th>
              <th class="tw:text-right">{{ $t('Amount') }}</th>
            </tr>
          </thead>
          <tbody>
            <OrderIndexTableRow
              v-for="order in models"
              :key="order.id"
              :order="order"
              :search="search"
              :target="props.target"
            />
          </tbody>
          <tfoot v-if="meta?.total_amount">
            <tr>
              <td colspan="6"></td>
              <td class="tw:text-right">
                <span class="tw:text-zinc-500">{{ $t('Total Amount:') }}</span><br>
                <span class="tw:font-bold">{{ formatMoney(meta.total_amount) }}</span><br>
                <small class="tw:text-zinc-500">{{ formatMoney(meta.total_paid_amount) }}</small>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </template>
  </SearchableModel>
</template>
